/**
 * Feedback Dialog Context
 * 
 * Provides Promise-based confirmation and prompt dialog systems that can be used
 * anywhere in the app via the useFeedback() hook.
 * 
 * Usage:
 *   const { confirm, prompt } = useFeedback();
 *   
 *   // Confirmation
 *   const shouldDelete = await confirm.delete('this item');
 *   if (shouldDelete) { ... }
 *   
 *   // Prompt
 *   const url = await prompt.url();
 *   if (url) { editor.setLink({ href: url }); }
 *   
 *   // Save or Discard
 *   const result = await confirm.saveOrDiscard();
 *   if (result === 'save') { await save(); proceed(); }
 *   else if (result === 'discard') { reset(); proceed(); }
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/** Blur the currently focused element to dismiss Safari/iCloud Keychain suggestions */
const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};
import { ConfirmDialog, ConfirmOptions } from '@/components/ui/confirm-dialog';
import { PromptDialog, PromptOptions } from '@/components/ui/prompt-dialog';
import { SaveOrDiscardDialog, SaveOrDiscardOptions, SaveOrDiscardResult } from '@/components/ui/save-or-discard-dialog';

interface FeedbackDialogContextType {
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
  showPrompt: (options: PromptOptions) => Promise<string | null>;
  showSaveOrDiscard: (options: SaveOrDiscardOptions) => Promise<SaveOrDiscardResult>;
}

const FeedbackDialogContext = createContext<FeedbackDialogContextType | null>(null);

export const useFeedbackDialogContext = () => {
  const context = useContext(FeedbackDialogContext);
  if (!context) {
    throw new Error('useFeedbackDialogContext must be used within a FeedbackDialogProvider');
  }
  return context;
};

// Legacy alias for backwards compatibility
export const useConfirmContext = useFeedbackDialogContext;

interface FeedbackDialogProviderProps {
  children: React.ReactNode;
}

export const FeedbackDialogProvider = ({ children }: FeedbackDialogProviderProps) => {
  // Confirm dialog state
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);

  // Prompt dialog state
  const [promptState, setPromptState] = useState<(PromptOptions & { open: boolean }) | null>(null);
  const promptResolverRef = useRef<((value: string | null) => void) | null>(null);

  // Save or Discard dialog state
  const [saveOrDiscardState, setSaveOrDiscardState] = useState<(SaveOrDiscardOptions & { open: boolean }) | null>(null);
  const saveOrDiscardResolverRef = useRef<((value: SaveOrDiscardResult) => void) | null>(null);

  // === CONFIRM DIALOG ===
  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      blurActiveElement();
      confirmResolverRef.current = resolve;
      setConfirmState({ ...options, open: true });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    confirmResolverRef.current?.(true);
    setConfirmState(null);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    confirmResolverRef.current?.(false);
    setConfirmState(null);
  }, []);

  // === PROMPT DIALOG ===
  const showPrompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      blurActiveElement();
      promptResolverRef.current = resolve;
      setPromptState({ ...options, open: true });
    });
  }, []);

  const handlePromptSubmit = useCallback((value: string) => {
    promptResolverRef.current?.(value);
    setPromptState(null);
  }, []);

  const handlePromptCancel = useCallback(() => {
    promptResolverRef.current?.(null);
    setPromptState(null);
  }, []);

  // === SAVE OR DISCARD DIALOG ===
  const showSaveOrDiscard = useCallback((options: SaveOrDiscardOptions): Promise<SaveOrDiscardResult> => {
    return new Promise((resolve) => {
      blurActiveElement();
      saveOrDiscardResolverRef.current = resolve;
      setSaveOrDiscardState({ ...options, open: true });
    });
  }, []);

  const handleSaveOrDiscardSave = useCallback(() => {
    saveOrDiscardResolverRef.current?.('save');
    setSaveOrDiscardState(null);
  }, []);

  const handleSaveOrDiscardDiscard = useCallback(() => {
    saveOrDiscardResolverRef.current?.('discard');
    setSaveOrDiscardState(null);
  }, []);

  const handleSaveOrDiscardCancel = useCallback(() => {
    saveOrDiscardResolverRef.current?.(null);
    setSaveOrDiscardState(null);
  }, []);

  return (
    <FeedbackDialogContext.Provider value={{ showConfirm, showPrompt, showSaveOrDiscard }}>
      {children}
      
      {/* Confirm Dialog */}
      {confirmState && (
        <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText || 'Cancel'}
          variant={confirmState.variant}
          onConfirm={handleConfirm}
          onCancel={handleConfirmCancel}
        />
      )}

      {/* Prompt Dialog */}
      {promptState && (
        <PromptDialog
          open={promptState.open}
          title={promptState.title}
          message={promptState.message}
          placeholder={promptState.placeholder}
          defaultValue={promptState.defaultValue}
          inputType={promptState.inputType}
          confirmText={promptState.confirmText}
          cancelText={promptState.cancelText}
          validate={promptState.validate}
          onSubmit={handlePromptSubmit}
          onCancel={handlePromptCancel}
        />
      )}

      {/* Save or Discard Dialog */}
      {saveOrDiscardState && (
        <SaveOrDiscardDialog
          open={saveOrDiscardState.open}
          title={saveOrDiscardState.title}
          message={saveOrDiscardState.message}
          saveText={saveOrDiscardState.saveText}
          discardText={saveOrDiscardState.discardText}
          onSave={handleSaveOrDiscardSave}
          onDiscard={handleSaveOrDiscardDiscard}
          onCancel={handleSaveOrDiscardCancel}
        />
      )}
    </FeedbackDialogContext.Provider>
  );
};

// Legacy alias for backwards compatibility
export const ConfirmProvider = FeedbackDialogProvider;
